import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AvatarPreset, Button } from "@ec2/ui";
import { Dices, Gift } from "lucide-react";
import { useT } from "@ec2/i18n/react";
import { useTRPC } from "../lib/api";
import { useSession } from "../state/session";
import { useActive } from "../state/hooks";
import { useContinueToGame } from "./IdentityScreen";
import { formatEventStart } from "../lib/time";
import { joinError, type LocalizedError } from "../lib/errors";
import { takeLeftEvent } from "../lib/leftEvent";
import { parsePendingResolve, storePendingResolve } from "../lib/pendingResolve";
import { ErrorLine, LoadingLine } from "../components/common";

const CODE_RE = /^[0-9A-HJKMNP-TV-Z]{6}$/;

/** Join mutation shared by manual entry and the /j deep link. */
export function useJoin() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // The player's name is generated in the app's language at join.
  const { locale } = useSession();
  const mutation = useMutation(trpc.event.join.mutationOptions());

  const join = async (
    rawCode: string,
    opts: { displayName?: string; onJoined?: () => Promise<void> } = {},
  ): Promise<LocalizedError | null> => {
    const joinCode = rawCode.trim().toUpperCase();
    if (!CODE_RE.test(joinCode)) return { key: "ec2.join.invalidCode" };
    try {
      await mutation.mutateAsync({
        joinCode,
        locale,
        ...(opts.displayName !== undefined && { displayName: opts.displayName }),
      });
      if (opts.onJoined) {
        // The join link: the name was picked before joining — straight on.
        await opts.onJoined();
        return null;
      }
      await queryClient.invalidateQueries();
      // Manual join: the identity reveal is the join moment (brief §6).
      void navigate({ to: "/identity" });
      return null;
    } catch (err) {
      return joinError(err);
    }
  };

  return { join, isJoining: mutation.isPending };
}

/** Manual join: type the 6-char event code (QR path goes via /j/$code). */
export function JoinScreen() {
  const t = useT();
  const { join, isJoining } = useJoin();
  const [code, setCode] = useState("");
  const [error, setError] = useState<LocalizedError | null>(null);
  const search = useSearch({ strict: false }) as { resolve?: string };
  // One-shot: read AND clear on mount so the flash never reappears.
  const [leftEvent] = useState(() => takeLeftEvent());

  // A /q /c /o target scanned before joining may arrive via ?resolve=… (fresh
  // tab / reload) — restore it into sessionStorage for the post-join dispatch.
  useEffect(() => {
    const pending = parsePendingResolve(search.resolve);
    if (pending) storePendingResolve(pending);
  }, [search.resolve]);

  const submit = async () => {
    setError(null);
    setError(await join(code));
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-6 pb-16">
      {leftEvent && (
        <p
          role="status"
          data-testid="ec-left-flash"
          className="rounded-(--radius-chip) border border-border bg-surface px-4 py-3 text-[13px] font-medium text-fg-secondary"
        >
          {t("ec2.join.left", { event: leftEvent })}
        </p>
      )}
      <div>
        <p className="font-display text-lg font-bold tracking-[-0.03em] text-fg">{t("ec2.common.appName")}</p>
        <h1 className="mt-4 font-display text-[30px] leading-[1.1] font-bold tracking-[-0.02em] text-fg">
          {t("ec2.join.title")}
        </h1>
        <p className="mt-2 text-base text-fg-secondary">{t("ec2.join.intro")}</p>
      </div>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-fg-secondary">{t("ec2.join.codeLabel")}</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            maxLength={6}
            data-testid="ec-join-code"
            className="rounded-(--radius-chip) border border-border bg-surface px-4 py-3.5 text-center font-display text-2xl font-bold tracking-[0.2em] text-fg uppercase focus-visible:outline-2 focus-visible:outline-primary"
          />
        </label>
        <ErrorLine error={error} />
        <Button type="submit" disabled={isJoining || code.length < 6} data-testid="ec-join-submit">
          {isJoining ? t("ec2.join.joining") : t("ec2.join.submit")}
        </Button>
      </form>
    </main>
  );
}

/**
 * /j/$code — QR / shared-link join. Shows the event and the player name it
 * offers (shuffle for another); the tap on "Let's go" joins with that name and
 * goes straight into the game. It used to join on load, and link scanners
 * (Outlook Safe Links, Google) open shared links in a real browser — every
 * shared invite added a phantom participant (prod, 2026-10-20). Scanners read;
 * they don't tap.
 */
export function JoinDeepLinkScreen({ code }: { code: string }) {
  const t = useT();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { locale } = useSession();
  const { active, user } = useActive();
  const { join, isJoining } = useJoin();
  const continueToGame = useContinueToGame();
  const [error, setError] = useState<LocalizedError | null>(null);
  const joinCode = code.trim().toUpperCase();
  const preview = useQuery({ ...trpc.event.preview.queryOptions({ joinCode }), retry: false });

  // Came from a quest QR scanned before joining: keep it for the post-join
  // dispatch (fresh tab / reload) and say what it pays.
  const search = useSearch({ strict: false }) as { resolve?: string };
  const pendingQuest = parsePendingResolve(search.resolve);
  useEffect(() => {
    if (pendingQuest) storePendingResolve(pendingQuest);
  }, [search.resolve]);
  const questPreview = useQuery({
    ...trpc.event.previewByQuestCode.queryOptions({ code: pendingQuest?.code ?? "" }),
    enabled: pendingQuest?.kind === "q",
    retry: false,
  });
  // Only a quest of THIS event is promised here.
  const questHint = questPreview.data?.joinCode === joinCode ? questPreview.data : null;

  // Already in this event (link opened again): straight back in, nothing to confirm.
  const alreadyIn = active?.event.joinCode === joinCode;
  useEffect(() => {
    if (alreadyIn) void navigate({ to: "/quests" });
  }, [alreadyIn]);

  const ended = preview.data ? preview.data.endsAt < new Date() : false;
  const canJoin = Boolean(preview.data) && !ended && !alreadyIn;
  const suggestion = useQuery({
    ...trpc.event.suggestIdentity.queryOptions({ joinCode, locale }),
    enabled: canJoin,
    staleTime: Infinity, // a name only changes when the player shuffles
  });

  const lookupError: LocalizedError | null = preview.error
    ? joinError(preview.error)
    : ended
      ? { key: "ec2.join.ended" }
      : null;
  const shown = error ?? lookupError;
  const identity = suggestion.data;

  const letsGo = async () => {
    if (!identity) return;
    setError(
      await join(joinCode, {
        displayName: identity.displayName,
        onJoined: () => continueToGame(user?.tourCompletedAt != null),
      }),
    );
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-6 pb-16 text-center">
      <p className="font-display text-lg font-bold tracking-[-0.03em] text-fg">{t("ec2.common.appName")}</p>
      {shown ? (
        <>
          <ErrorLine error={shown} className="text-base" />
          <JoinFallbackLink />
        </>
      ) : preview.data && canJoin ? (
        <div className="flex flex-col items-center gap-4" data-testid="ec-join-confirm">
          <div>
            <p className="text-[13px] font-medium text-fg-secondary">{t("ec2.join.confirm.youreInvited")}</p>
            <h1 className="mt-1 font-display text-[26px] leading-[1.15] font-bold tracking-[-0.02em] text-fg">
              {preview.data.name}
            </h1>
            <p className="mt-1 text-sm text-fg-secondary">
              {t(preview.data.startsAt > new Date() ? "ec2.join.confirm.starts" : "ec2.join.confirm.running", {
                when: formatEventStart(preview.data.startsAt, locale, preview.data.timezone),
              })}
            </p>
          </div>
          {questHint && (
            <p
              className="flex w-full items-center gap-3 rounded-(--radius-card) border border-border bg-surface px-4 py-3 text-left text-sm text-fg"
              data-testid="ec-join-quest-hint"
            >
              <Gift size={20} className="shrink-0 text-bucks" aria-hidden />
              <span>
                {questHint.bucksReward > 0
                  ? t("ec2.join.confirm.questBucks", { quest: questHint.questName, bucks: questHint.bucksReward })
                  : t("ec2.join.confirm.quest", { quest: questHint.questName })}
              </span>
            </p>
          )}
          {identity ? (
            <div className="mt-2 flex w-full flex-col items-center gap-3" data-testid="ec-join-identity">
              <p className="text-[13px] font-medium text-fg-secondary">{t("ec2.join.confirm.youPlayAs")}</p>
              <div key={identity.avatarPresetId + identity.displayName} className="ec-bracket-snap">
                <AvatarPreset
                  preset={identity.avatarPresetId}
                  size={88}
                  label={t("ec2.a11y.avatar", { name: identity.displayName })}
                />
              </div>
              <p className="font-display text-[24px] leading-[1.15] font-bold text-fg" data-testid="ec-identity-name">
                {identity.displayName}
              </p>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => void suggestion.refetch()}
                disabled={suggestion.isFetching || isJoining}
                data-testid="ec-identity-shuffle"
              >
                <Dices size={20} aria-hidden />
                {t("ec2.identity.shuffle")}
              </Button>
              <Button
                variant="primary"
                className="w-full"
                disabled={isJoining || suggestion.isFetching}
                data-testid="ec-join-confirm-button"
                onClick={() => void letsGo()}
              >
                {isJoining ? t("ec2.join.joining") : t("ec2.identity.letsGo")}
              </Button>
            </div>
          ) : (
            <LoadingLine />
          )}
        </div>
      ) : (
        <LoadingLine />
      )}
    </main>
  );
}

function JoinFallbackLink() {
  const t = useT();
  const navigate = useNavigate();
  return (
    <Button variant="ghost" onClick={() => void navigate({ to: "/join" })}>
      {t("ec2.join.title")}
    </Button>
  );
}
